/**
 * Pattern 15 on a table (TMMAPP-0130): the same pointer, touch and keyboard model as
 * `SortableCardGrid`, with a vertical list strategy so rows slide in one column. The overlay is
 * portaled to `document.body` above dialogs. Reduced motion drops the animation and keeps the
 * overlay. A rejected reorder restores focus to the handle.
 */
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  type DropAnimation,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { labelCanvasSx } from '@/theme/textLabel';
import { resolveTokens } from '@/theme/theme';
import { ReorderHandle, type ReorderHandleProps } from './SortableCardGrid';

const POINTER_OPTIONS = { activationConstraint: { distance: 8 } };
const TOUCH_OPTIONS = { activationConstraint: { delay: 200, tolerance: 8 } };
const KEYBOARD_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };
const DRAG_OVERLAY_CLASS = 'tmm-drag-overlay';
const MOTION_MS = 200;
const SORTABLE_TRANSITION = { duration: MOTION_MS, easing: 'ease' };
const DROP_ANIMATION: DropAnimation = {
  duration: MOTION_MS,
  easing: 'ease',
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { visibility: 'hidden' } } }),
};

export interface SortableTableProps<T> {
  items: readonly T[];
  getId: (item: T) => string | number;
  getName: (item: T) => string;
  /** Rows outside the reorder set render after the sortable ones, with no handle. */
  isSortable?: (item: T) => boolean;
  canReorder: boolean;
  onReorder: (ids: Array<string | number>) => void | Promise<void>;
  /** Column headers, including the order column when handles are on. Ignored for `stack`. */
  head: ReactNode;
  /** `table` from `md` up. `stack` is the narrow directory: one outlined row per payee. */
  layout?: 'table' | 'stack';
  /** Accessible name of the table or the stack. */
  label: string;
  onRowClick?: (item: T) => void;
  renderCells: (item: T, handle: ReorderHandleProps | null) => ReactNode;
}

export function SortableTable<T>(props: SortableTableProps<T>) {
  if (!props.canReorder) return <StaticRows {...props} />;
  return <ReorderableRows {...props} />;
}

function splitRows<T>(
  items: readonly T[],
  isSortable: ((item: T) => boolean) | undefined,
): { sortable: T[]; fixed: T[] } {
  if (isSortable === undefined) return { sortable: [...items], fixed: [] };
  return {
    sortable: items.filter((item) => isSortable(item)),
    fixed: items.filter((item) => !isSortable(item)),
  };
}

function sameOrder(
  left: readonly (string | number)[],
  right: readonly (string | number)[],
): boolean {
  return (
    left.length === right.length && left.every((id, index) => String(id) === String(right[index]))
  );
}

function translateOf(transform: { x: number; y: number } | null): string | undefined {
  if (transform === null) return undefined;
  return `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`;
}

function StaticRows<T>({
  items,
  getId,
  renderCells,
  head,
  label,
  layout = 'table',
  onRowClick,
}: SortableTableProps<T>) {
  if (layout === 'stack') {
    return (
      <Stack spacing={1} aria-label={label}>
        {items.map((item) => (
          <Paper
            key={String(getId(item))}
            variant="outlined"
            data-payee-row={String(getId(item))}
            onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
            sx={{ p: 1.5, cursor: onRowClick === undefined ? undefined : 'pointer' }}
          >
            {renderCells(item, null)}
          </Paper>
        ))}
      </Stack>
    );
  }
  return (
    <Table size="small" aria-label={label}>
      <TableHead>{head}</TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={String(getId(item))}
            hover
            data-payee-row={String(getId(item))}
            onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
            sx={onRowClick === undefined ? undefined : { cursor: 'pointer' }}
          >
            {renderCells(item, null)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ReorderableRows<T>({
  items,
  getId,
  getName,
  isSortable,
  onReorder,
  renderCells,
  head,
  label,
  layout = 'table',
  onRowClick,
}: SortableTableProps<T>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const instructionsId = useId();
  const instructions = t('reorder.instructions');
  const sensors = useSensors(
    useSensor(MouseSensor, POINTER_OPTIONS),
    useSensor(TouchSensor, TOUCH_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_OPTIONS),
  );
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const handles = useRef(new Map<string, HTMLElement>());
  const itemsRef = useRef(items);
  const getIdRef = useRef(getId);
  const getNameRef = useRef(getName);
  const isSortableRef = useRef(isSortable);
  useLayoutEffect(() => {
    itemsRef.current = items;
    getIdRef.current = getId;
    getNameRef.current = getName;
    isSortableRef.current = isSortable;
  });

  const { sortable, fixed } = splitRows(items, isSortable);
  const sortableIds = sortable.map((item) => getId(item));

  const nameOf = (id: UniqueIdentifier) => {
    const item = itemsRef.current.find((entry) => String(getIdRef.current(entry)) === String(id));
    return item === undefined ? String(id) : getNameRef.current(item);
  };

  const landing = (overId: UniqueIdentifier) => {
    const idOf = getIdRef.current;
    const rows =
      isSortableRef.current === undefined
        ? [...itemsRef.current]
        : itemsRef.current.filter((item) => isSortableRef.current!(item));
    const overIndex = rows.findIndex((item) => String(idOf(item)) === String(overId));
    if (overIndex < 0) return undefined;
    return { position: overIndex + 1, total: rows.length };
  };

  const focusMoved = (id: UniqueIdentifier) => {
    const key = String(id);
    const fromMap = handles.current.get(key);
    const fromDom = document.querySelector<HTMLElement>(
      `[data-reorder-handle][data-reorder-id="${CSS.escape(key)}"]`,
    );
    const node = fromMap?.isConnected ? fromMap : fromDom?.isConnected ? fromDom : undefined;
    node?.focus();
  };

  const commit = (nextIds: Array<string | number>, focusId: UniqueIdentifier) => {
    if (sameOrder(sortableIds, nextIds)) return;
    void Promise.resolve(onReorder(nextIds)).catch(() => {
      focusMoved(focusId);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusMoved(focusId);
        });
      });
    });
  };

  const indexOf = (id: UniqueIdentifier) =>
    sortable.findIndex((item) => String(getId(item)) === String(id));

  const moveBy = (id: UniqueIdentifier, delta: -1 | 1) => {
    const from = indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= sortable.length) return;
    commit(
      arrayMove(sortable, from, to).map((item) => getId(item)),
      id,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const from = indexOf(active.id);
    const to = indexOf(over.id);
    if (from < 0 || to < 0 || from === to) return;
    commit(
      arrayMove(sortable, from, to).map((item) => getId(item)),
      active.id,
    );
  };

  const activeItem =
    activeId === null
      ? undefined
      : sortable.find((item) => String(getId(item)) === String(activeId));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{
        screenReaderInstructions: { draggable: instructions },
        announcements: {
          onDragStart({ active }) {
            return t('reorder.pickedUp', { name: nameOf(active.id) });
          },
          onDragOver({ active, over }) {
            if (!over || String(over.id) === String(active.id)) return undefined;
            const spot = landing(over.id);
            if (spot === undefined) return undefined;
            return t('reorder.moved', {
              name: nameOf(active.id),
              position: spot.position,
              total: spot.total,
            });
          },
          onDragEnd({ active }) {
            return t('reorder.dropped', { name: nameOf(active.id) });
          },
          onDragCancel() {
            return t('reorder.cancelled');
          },
        },
      }}
      onDragStart={(event: DragStartEvent) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <Box id={instructionsId} sx={visuallyHidden}>
        {instructions}
      </Box>
      {layout === 'stack' ? (
        <Stack spacing={1} aria-label={label}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {sortable.map((item, index) => {
              const id = getId(item);
              return (
                <SortableStackRow
                  key={String(id)}
                  id={id}
                  index={index}
                  count={sortable.length}
                  name={getName(item)}
                  instructionsId={instructionsId}
                  reduceMotion={reduceMotion}
                  registerHandle={(element) => {
                    const key = String(id);
                    if (element) handles.current.set(key, element);
                    else handles.current.delete(key);
                  }}
                  onMoveEarlier={() => moveBy(id, -1)}
                  onMoveLater={() => moveBy(id, 1)}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
                >
                  {(handle) => renderCells(item, handle)}
                </SortableStackRow>
              );
            })}
          </SortableContext>
          {fixed.map((item) => (
            <Paper
              key={String(getId(item))}
              variant="outlined"
              data-payee-row={String(getId(item))}
              onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
              sx={{ p: 1.5, cursor: onRowClick === undefined ? undefined : 'pointer' }}
            >
              {renderCells(item, null)}
            </Paper>
          ))}
        </Stack>
      ) : (
        <Table size="small" aria-label={label}>
          <TableHead>{head}</TableHead>
          <TableBody>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {sortable.map((item, index) => {
                const id = getId(item);
                return (
                  <SortableTableRow
                    key={String(id)}
                    id={id}
                    index={index}
                    count={sortable.length}
                    name={getName(item)}
                    instructionsId={instructionsId}
                    reduceMotion={reduceMotion}
                    registerHandle={(element) => {
                      const key = String(id);
                      if (element) handles.current.set(key, element);
                      else handles.current.delete(key);
                    }}
                    onMoveEarlier={() => moveBy(id, -1)}
                    onMoveLater={() => moveBy(id, 1)}
                    onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
                  >
                    {(handle) => renderCells(item, handle)}
                  </SortableTableRow>
                );
              })}
            </SortableContext>
            {fixed.map((item) => (
              <TableRow
                key={String(getId(item))}
                hover
                data-payee-row={String(getId(item))}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
                sx={onRowClick === undefined ? undefined : { cursor: 'pointer' }}
              >
                {renderCells(item, null)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {createPortal(
        <DragOverlay
          dropAnimation={reduceMotion ? null : DROP_ANIMATION}
          className={DRAG_OVERLAY_CLASS}
          zIndex={theme.zIndex.modal + 1}
        >
          {activeItem !== undefined && (
            <Paper
              aria-hidden
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                boxShadow: tokens.elevation.dialog,
                borderRadius: `${tokens.radius.card}px`,
                bgcolor: tokens.surface,
                ...labelCanvasSx(tokens, 'surface'),
              }}
            >
              {getName(activeItem)}
            </Paper>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function SortableTableRow({
  id,
  index,
  count,
  name,
  instructionsId,
  reduceMotion,
  registerHandle,
  onMoveEarlier,
  onMoveLater,
  onClick,
  children,
}: {
  id: UniqueIdentifier;
  index: number;
  count: number;
  name: string;
  instructionsId: string;
  reduceMotion: boolean;
  registerHandle: (element: HTMLElement | null) => void;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  onClick?: (() => void) | undefined;
  children: (handle: ReorderHandleProps) => ReactNode;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const sortableAttributes = { roleDescription: t('reorder.roleDescriptionRow') };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable(
    reduceMotion
      ? {
          id,
          attributes: sortableAttributes,
          transition: null,
          animateLayoutChanges: () => false,
        }
      : { id, attributes: sortableAttributes, transition: SORTABLE_TRANSITION },
  );
  const handle: ReorderHandleProps = {
    id,
    name,
    canMoveEarlier: index > 0,
    canMoveLater: index < count - 1,
    onMoveEarlier,
    onMoveLater,
    instructionsId,
    attributes,
    listeners,
    setActivatorNodeRef: (element) => {
      registerHandle(element);
      setActivatorNodeRef(element);
    },
  };
  return (
    <TableRow
      ref={setNodeRef}
      hover
      data-payee-row={String(id)}
      data-sortable-id={String(id)}
      data-sortable-index={index}
      onClick={onClick}
      style={{
        transform: translateOf(transform),
        transition: reduceMotion ? undefined : transition,
      }}
      sx={{
        cursor: onClick === undefined ? undefined : 'pointer',
        ...(isDragging
          ? {
              outline: `1px dashed ${tokens.lineStrong}`,
              bgcolor: tokens.surface2,
              '& > *': { visibility: 'hidden' },
            }
          : {}),
      }}
    >
      {children(handle)}
    </TableRow>
  );
}

/** The same grab, keyboard and long-press as a table row, on one outlined directory card. */
function SortableStackRow({
  id,
  index,
  count,
  name,
  instructionsId,
  reduceMotion,
  registerHandle,
  onMoveEarlier,
  onMoveLater,
  onClick,
  children,
}: {
  id: UniqueIdentifier;
  index: number;
  count: number;
  name: string;
  instructionsId: string;
  reduceMotion: boolean;
  registerHandle: (element: HTMLElement | null) => void;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  onClick?: (() => void) | undefined;
  children: (handle: ReorderHandleProps) => ReactNode;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const sortableAttributes = { roleDescription: t('reorder.roleDescriptionRow') };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable(
    reduceMotion
      ? {
          id,
          attributes: sortableAttributes,
          transition: null,
          animateLayoutChanges: () => false,
        }
      : { id, attributes: sortableAttributes, transition: SORTABLE_TRANSITION },
  );
  const handle: ReorderHandleProps = {
    id,
    name,
    canMoveEarlier: index > 0,
    canMoveLater: index < count - 1,
    onMoveEarlier,
    onMoveLater,
    instructionsId,
    attributes,
    listeners,
    setActivatorNodeRef: (element) => {
      registerHandle(element);
      setActivatorNodeRef(element);
    },
  };
  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      data-payee-row={String(id)}
      data-sortable-id={String(id)}
      data-sortable-index={index}
      onClick={onClick}
      style={{
        transform: translateOf(transform),
        transition: reduceMotion ? undefined : transition,
      }}
      sx={{
        p: 1.5,
        cursor: onClick === undefined ? undefined : 'pointer',
        ...(isDragging
          ? {
              outline: `1px dashed ${tokens.lineStrong}`,
              bgcolor: tokens.surface2,
              '& > *': { visibility: 'hidden' },
            }
          : {}),
      }}
    >
      {children(handle)}
    </Paper>
  );
}

/**
 * The first cell while ordering. Wide enough for the 40px handle: a small table's checkbox
 * column is 24px and paints the next cell over the grab target.
 */
export function OrderHandleCell({ handle }: { handle: ReorderHandleProps | null }) {
  return (
    <TableCell onClick={(event) => event.stopPropagation()} sx={{ width: 56, px: 0.5 }}>
      {handle !== null && <ReorderHandle {...handle} />}
    </TableCell>
  );
}
